// ErrorBoundary — catches any error thrown during render of its
// children and shows a fallback UI INSTEAD of unmounting the entire
// app. Without this, a ReferenceError in any one page (e.g. an
// undefined variable) blanks the whole site until the user hard-
// refreshes — exactly the failure mode the user reported.
//
// Wrap every <Routes> in App.tsx with this. Each route's content
// renders inside the boundary so a thrown error in one page leaves
// the top bar / bottom bar / shell intact and gives the user a
// "Go home" link to recover without a hard refresh.
//
// React error boundaries MUST be class components — there's no hooks
// equivalent. The implementation is small.

import { Component, ErrorInfo, ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { error: Error | null };

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console so dev tools shows a real stack trace.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] page render threw:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-xl mx-auto px-4 py-12 text-center">
          <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30 p-6">
            <div className="text-base font-bold text-rose-800 dark:text-rose-200 mb-2">
              This page hit an error
            </div>
            <p className="text-sm text-rose-700 dark:text-rose-200/80 mb-4">
              {this.state.error.message || 'Something went wrong while rendering.'}
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => this.setState({ error: null })}
                className="rounded-full bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold px-4 py-2"
              >
                Try again
              </button>
              <a
                href="/"
                className="rounded-full bg-white dark:bg-neutral-900 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200 text-xs font-bold px-4 py-2"
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
